import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao, Indicador, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { BuscaRemota, Rotulo, Selecao } from '../../componentes/formulario';
import { get } from '../../nucleo/api';
import { data, hojeIso, mesAno, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_SITUACAO, horas, ultimosMeses, type Espelho as TipoEspelho } from './tipos';

/** Espelho de ponto por funcionário e mês: o esperado pela jornada contra o trabalhado, dia a dia, com os totais e o resultado. */
export function Espelho() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const todos = s.pode('ponto.ver_todos');
  const meses = ultimosMeses(hojeIso(), 13);
  const [mes, setMes] = useState(meses[0]!);
  const [funcionario, setFuncionario] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['ponto', 'espelho', funcionario, mes],
    queryFn: () => get<TipoEspelho | null>('/ponto/espelho', { ano_mes: mes, ...(funcionario ? { funcionario_id: funcionario } : {}) }),
  });
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        {todos && (
          <div className="w-full min-w-0 sm:w-80">
            <Rotulo id="ponto-espelho-funcionario" rotulo={t('campos.funcionario')}>
              <BuscaRemota id="ponto-espelho-funcionario" def={{ caminho: '/pessoas/funcionarios', rotulo: (i) => String(i.nome ?? ''), pessoa: true }} valor={funcionario} aoMudar={(v) => setFuncionario(v)} />
            </Rotulo>
          </div>
        )}
        <div className="w-full min-w-0 sm:w-56">
          <Rotulo id="ponto-espelho-mes" rotulo={t('campos.mes')}>
            <Selecao id="ponto-espelho-mes" value={mes} onChange={(e) => setMes(e.target.value)} opcoes={meses.map((m) => ({ valor: m, rotulo: mesAno(m) }))} />
          </Rotulo>
        </div>
      </div>
      <Estado consulta={q}>
        {(e) => {
          if (!e) return <Vazio texto={todos ? t('vazio.espelho') : t('vazio.semFuncionario')} />;
          const r = e.resultado;
          return (
            <>
              <div className="grid min-w-0 grid-cols-2 gap-3 md:grid-cols-4">
                <Indicador rotulo={t('campos.trabalhado')} valor={horas(e.totais.trabalhado_min)} detalhe={`${t('campos.esperado')} ${horas(e.totais.esperado_min)}`} />
                <Indicador rotulo={t('campos.extra50')} valor={horas(e.totais.extra50_min)} detalhe={`${t('campos.extra100')} ${horas(e.totais.extra100_min)}`} cor="verde" />
                <Indicador rotulo={t('campos.debito')} valor={horas(e.totais.debito_min)} cor="laranja" />
                <Indicador rotulo={t('campos.faltasDias')} valor={String(e.totais.faltas_dias)} detalhe={`${t('campos.abonados')} ${e.totais.abonados_dias}`} cor="perigo" />
              </div>
              <Cartao titulo={`${nomePessoa(e.funcionario_nome)} · ${mesAno(mes)}`}>
                <ul className="mb-3 flex flex-col gap-1 text-sm text-tinta-3">
                  <li>{t('regimeDe', { regime: t(`regime.${e.regime}`) })}</li>
                  <li>{t('toleranciaDe', { marcacao: e.tolerancia_marcacao_min, dia: e.tolerancia_dia_min })}</li>
                  {!e.ausencias_ligado && <li>{t('semAusencias')}</li>}
                  {e.ajustes_pendentes > 0 && <li className="text-laranja">{t('pendentes', { count: e.ajustes_pendentes })}</li>}
                </ul>
                <div role="region" aria-label={t('abas.espelho')} tabIndex={0} className="relative overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className="px-2 py-2 font-medium">{t('campos.dia')}</th>
                        <th scope="col" className="px-2 py-2 font-medium">{t('campos.marcacoes')}</th>
                        <th scope="col" className="px-2 py-2 text-right font-medium">{t('campos.esperado')}</th>
                        <th scope="col" className="px-2 py-2 text-right font-medium">{t('campos.trabalhado')}</th>
                        <th scope="col" className="px-2 py-2 text-right font-medium">{t('campos.extra50')}</th>
                        <th scope="col" className="px-2 py-2 text-right font-medium">{t('campos.extra100')}</th>
                        <th scope="col" className="px-2 py-2 text-right font-medium">{t('campos.debito')}</th>
                        <th scope="col" className="px-2 py-2 font-medium">{t('campos.situacao')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.dias.map((d) => (
                        <tr key={d.data} className="border-b border-borda last:border-0">
                          <td className="px-2 py-2 whitespace-nowrap">{data(d.data)}</td>
                          <td className="numero px-2 py-2">{d.marcacoes.join(' ') || '-'}</td>
                          <td className="numero px-2 py-2 text-right">{d.esperado_min ? horas(d.esperado_min) : '-'}</td>
                          <td className="numero px-2 py-2 text-right">{d.trabalhado_min ? horas(d.trabalhado_min) : '-'}</td>
                          <td className="numero px-2 py-2 text-right">{d.extra50_min ? horas(d.extra50_min) : '-'}</td>
                          <td className="numero px-2 py-2 text-right">{d.extra100_min ? horas(d.extra100_min) : '-'}</td>
                          <td className="numero px-2 py-2 text-right">{d.debito_min || d.falta_min ? horas(d.debito_min + d.falta_min) : '-'}</td>
                          <td className="px-2 py-2">
                            <span className="flex flex-wrap gap-1">
                              <Selo cor={COR_SITUACAO[d.situacao]}>{t(`situacao.${d.situacao}`)}</Selo>
                              {d.tolerancia_aplicada && <span className="text-xs text-tinta-3">{t('tolerancia')}</span>}
                              {d.intervalo_curto && <span className="text-xs text-laranja">{t('intervaloCurto')}</span>}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Cartao>
              <Cartao titulo={t('resultado')}>
                <dl className="grid gap-x-6 text-sm sm:grid-cols-2">
                  {(
                    [
                      ['folhaHe50', r.he50_min],
                      ['folhaHe100', r.he100_min],
                      ['folhaDesconto', r.desconto_min],
                      ['bancoCredito', r.banco_credito_min],
                      ['bancoDebito', r.banco_debito_min],
                    ] as const
                  ).map(([k, v]) => (
                    <div key={k} className="flex flex-wrap justify-between gap-2 border-b border-borda py-2">
                      <dt className="text-tinta-3">{t(`campos.${k}`)}</dt>
                      <dd className="numero text-tinta-1">{horas(v)}</dd>
                    </div>
                  ))}
                </dl>
              </Cartao>
            </>
          );
        }}
      </Estado>
    </div>
  );
}
